import { XMLParser } from 'fast-xml-parser'
import OLFormatGeoJSON from 'ol/format/GeoJSON'
import OLFormatGML3 from 'ol/format/GML3'

import type { GeoJSONObject } from 'ol/format/GeoJSON'

import PoziAPIData from 'api/data'

/**
 * Handle data requests
 */
export const getDataAsGeoJSON = function (url: string) {
  return new Promise(function (resolve, reject) {
    let errorMessage = ''

    if (!url) {
      errorMessage = 'PoziApiGml - .getDataAsGeoJSON(url) Invalid URL'
      return reject(errorMessage)
    }

    // console.log('PoziApiGml - .getDataAsGeoJSON(url): ', url)

    // Fetching the data as text
    PoziAPIData().ajax({
      dataType: 'text',
      url,
      // @ts-expect-error
      error: function (_jqXHR, _textStatus, errorThrown) {
        console.log('LoaderFailed url', url)
        reject(errorThrown)
      },
      success: function (xml: string) {
        handleGmlFormat(xml)
          .then(geojson => resolve(geojson))
          .catch(function (err) {
            console.log('handleGmlFormat error: ', err)
            return reject(err)
          })
      }
    })
  })
}

/**
 * Handle data requests
 */
const handleGmlFormat = function (xml: string) {
  return new Promise(function (resolve, reject) {
    let err = ''

    // console.debug('handleGmlFormat raw data', xml)

    // We create a little snippet to detect whether or not the result is an error
    // and also the projection (srsName) of the data returned
    const xmlSnippet = xml.slice(0, 2000)

    // Handle errors within the GML
    if (xmlSnippet.includes('ServiceException')) {
      console.log('handleGmlFormat - ServiceException detected in xml', xmlSnippet)

      // Use fast-xml-parser
      const parserOptions = {
        attributeNamePrefix: '',
        ignoreAttributes: false,
        removeNSPrefix: true,
        textNodeName: '_text'
      }

      // Extract exception code and text from the XML
      const xmlParser = new XMLParser(parserOptions)
      const xmlObj = xmlParser.parse(xml)
      const serviceException = xmlObj.ServiceExceptionReport?.ServiceException || {}
      const { code, _text } = serviceException

      let fullErrorMessage = _text || 'Unknown Error'
      if (code) {
        fullErrorMessage += ` (${code})`
      }

      reject(fullErrorMessage)

      return
    }

    // Try to extract the srsName from the GML string
    const gmlProjection = xmlSnippet.match(/srsName="(.*?)"/)?.[1]

    // console.debug('LoaderGML - success xml', gmlProjection, xmlSnippet)

    let geojson: GeoJSONObject

    try {
      // We need to explicitly mention the dataProjection (if it's different
      // from EPSG:4326) as OL currently (as of May 2023) does not support
      // reading the projection from the GML.
      //
      // See:
      // - https://github.com/openlayers/openlayers/issues/8455
      // - https://github.com/openlayers/openlayers/issues/8456

      const gmlReader = new OLFormatGML3()
      const features = gmlReader.readFeatures(xml, {
        dataProjection: gmlProjection || 'EPSG:4326',
        featureProjection: 'EPSG:4326'
      })

      // Clean up the features somewhat:
      // - remove the 'boundedBy' field
      // - fix any fields that have {_content_: undefined, xsi:nil: 'true'} values
      //   (that should have been null fields. Issue with QGIS < 3.30)
      for (const feature of features) {
        // Remove 'boundedBy' field
        feature.unset('boundedBy')

        // Replace field values that have 'xsi:nil' with null
        const newProps = feature.getProperties()
        for (const [key, value] of Object.entries(newProps)) {
          if (typeof value === 'object' && value?.['xsi:nil']) {
            newProps[key] = null
          }
        }

        // Replace properties with cleaned up ones
        feature.setProperties(newProps)
      }

      // Then convert OL Features to GeoJSON
      // Note: this is somewhat inefficient and we may want to improve
      //       the conversion from XML to Features to GeoJSON to Features
      //       in the future
      //
      const olFormatGeoJSON = new OLFormatGeoJSON()
      geojson = olFormatGeoJSON.writeFeaturesObject(features)

      // console.debug('handleGmlFormat - features', features, geojson)
    } catch (e) {
      err = 'Error parsing data'
      return reject(err)
    }

    if (!geojson) {
      err = 'Unable to find key properties to raw data.'
      return reject(err)
    }

    return resolve(geojson)
  })
}
